<script setup lang="ts">
import { computed, ref } from 'vue';
import { Listbox, ListboxButton, ListboxLabel, ListboxOption, ListboxOptions } from '@headlessui/vue';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/vue/20/solid';
import { nanoid } from 'nanoid';
import { TippyDirective } from '@/helpers/tippy';

const vTippy = TippyDirective;
const props = withDefaults(
  defineProps<{
    id?: string;
    label: string;
    labelType?: 'default' | 'overlapping' | 'floating';
    modelValue?: string[];
    listItems: Record<string, string>;
    errorMessage?: string;
    highlightError?: boolean;
    disabled?: boolean;
    tooltip?: string;
  }>(),
  {
    id: undefined,
    labelType: 'default',
    modelValue: () => [],
    errorMessage: undefined,
    highlightError: false,
    disabled: false,
    tooltip: undefined,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void;
}>();

const inputId = computed(() => props.id ?? nanoid(8));

const listOptions = computed(() => Object.entries(props.listItems).map(([id, label]) => ({ id, label })));

const selectedLabels = computed(() => {
  const selected = props.modelValue ?? [];
  return selected.map((id) => props.listItems[id]).filter(Boolean);
});

const displayValue = computed(() => selectedLabels.value.join(', '));

const wrapperRef = ref<HTMLDivElement>();
function handleFocusOut(event: Event) {
  const target = (event as FocusEvent).relatedTarget as HTMLElement;
  if (target && wrapperRef.value?.contains(target)) {
    event.stopImmediatePropagation();
  }
}

const isSelected = (id: string) => (props.modelValue ?? []).includes(id);
function handleUpdate(selectedIds: string[]) {
  emit('update:modelValue', selectedIds);
}
</script>

<template>
  <div ref="wrapperRef">
    <Listbox
      v-slot="{ open }"
      :model-value="modelValue ?? []"
      as="div"
      multiple
      :disabled="disabled"
      @update:model-value="handleUpdate"
      @focusout="handleFocusOut"
    >
      <ListboxLabel
        v-if="labelType === 'default'"
        class="block text-sm font-medium text-gray-700"
        :class="tooltip ? 'inline-flex items-center gap-1' : 'inline-block'"
      >
        {{ label }}
      </ListboxLabel>
      <div class="relative my-1">
        <ListboxButton
          :id="inputId"
          :data-test-id="inputId"
          class="relative w-full cursor-default rounded-md border border-gray-300 bg-white pr-9 text-left shadow-sm focus:border-bridgit-royalBlue focus:outline-none focus:ring-1 focus:ring-bridgit-royalBlue disabled:bg-gray-100 sm:text-sm"
          :class="errorMessage ? '!border-rose-500 focus:!border-rose-500 focus:!ring-rose-500' : null"
        >
          <span class="block h-10 truncate px-3 py-2 text-base" :class="{ 'text-gray-500': !displayValue }">
            {{ displayValue }}
          </span>
          <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
          </span>
        </ListboxButton>

        <ListboxLabel
          v-if="labelType !== 'default'"
          class="pointer-events-none absolute -top-2 left-2 -mt-px bg-white px-1 text-xs font-medium text-gray-900"
          :class="[
            tooltip ? 'inline-flex items-center gap-1' : 'inline-block',
            labelType === 'floating'
              ? [
                  'transition-all',
                  !(modelValue ?? []).length && !open ? '!top-2 !mt-0 !text-base !font-normal !text-gray-700' : '',
                ]
              : [],
            errorMessage ? 'text-rose-500' : '',
          ]"
        >
          <span>{{ label }}</span>
          <button
            v-if="tooltip"
            v-tippy="{ content: tooltip, trigger: 'mouseenter focus', placement: 'right' }"
            type="button"
            class="pointer-events-auto shrink-0 text-bridgit-lightGrey hover:text-bridgit-darkGrey"
            :aria-label="`${label} information`"
            @click.prevent
          >
            <BxIcon name="circleInfoLight" class="h-4 w-4" />
          </button>
        </ListboxLabel>

        <transition
          leave-active-class="transition ease-in duration-100"
          leave-from-class="opacity-100"
          leave-to-class="opacity-0"
        >
          <div
            v-if="open"
            class="absolute z-40 mt-1 w-full rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5"
          >
            <ListboxOptions
              static
              class="max-h-56 w-full overflow-auto rounded-t-md py-1 text-base focus:outline-none sm:text-sm"
            >
              <ListboxOption
                v-for="item in listOptions"
                :id="`${inputId}-${item.id}`"
                :key="item.id"
                v-slot="{ active }"
                :data-test-id="`${inputId}-${item.id}`"
                as="template"
                :value="item.id"
              >
                <li
                  :class="[
                    active ? 'bg-bridgit-royalBlue text-white' : 'text-gray-900',
                    'relative cursor-default select-none py-2 pl-3 pr-9',
                  ]"
                >
                  <span
                    :id="`${inputId}-${item.id}-label`"
                    :data-test-id="`${inputId}-${item.id}-label`"
                    :class="[isSelected(item.id) ? 'font-semibold' : 'font-normal', 'block']"
                  >
                    {{ item.label }}
                  </span>
                  <span
                    v-if="isSelected(item.id)"
                    :class="[
                      active ? 'text-white' : 'text-bridgit-royalBlue',
                      'absolute inset-y-0 right-0 flex items-center pr-4',
                    ]"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
            <div class="border-t border-gray-200 p-2">
              <button
                type="button"
                class="w-full rounded-md bg-bridgit-royalBlue px-3 py-2 text-sm font-medium text-white hover:bg-bridgit-royalBlue/90"
              >
                Apply selection
              </button>
            </div>
          </div>
        </transition>
      </div>
    </Listbox>

    <transition :enter-active-class="!highlightError ? 'animate-fadeIn' : ''">
      <p
        v-show="!!errorMessage"
        :id="`${inputId}-error`"
        :data-test-id="`${inputId}-error`"
        class="text-sm text-rose-500"
        :class="{ 'animate-shakeX': highlightError }"
      >
        {{ errorMessage }}
      </p>
    </transition>
  </div>
</template>
